import React, { useState } from 'react';
import { useMediaQuery } from "react-responsive"; // Importing useMediaQuery hook from react-responsive
import { 
  FaHome, 
  FaClock, 
  FaLinkedin, 
  FaTwitter, 
  FaFacebook, 
  FaInstagram, 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt, 
  FaWhatsapp, 
  FaSkype, 
  FaComments,
  FaPhoneAlt,
  FaCheck
} from "react-icons/fa";
import { MdMarkEmailRead, MdLocationOn, MdExpandMore, MdExpandLess, MdSupportAgent } from "react-icons/md";
import { IoIosPhonePortrait } from "react-icons/io";
import { ChakraProvider } from '@chakra-ui/react';
import { PhoneIcon, EmailIcon, InfoOutlineIcon, WarningIcon, AddIcon } from '@chakra-ui/icons';
import { Box, Flex, VStack, HStack, Text, Icon, Container, Heading, Divider, SimpleGrid, IconButton, useColorModeValue, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Avatar, AvatarGroup, Button } from '@chakra-ui/react';
import { Image } from '@chakra-ui/react';
import Appointment from './Appointment';

const Contact = ({ isEmbeddedAsForm = false }) => {
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 704px)" });
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');
  const [expandedIndex, setExpandedIndex] = useState(-1);
  const primaryColor = useColorModeValue("blue.500", "teal.500");
  const secondaryColor = useColorModeValue("teal.500", "blue.500");

  const handlePhoneClick = () => {
    window.location.href = 'tel:+919473421755';
  };

  const handleEmailClick = () => {
    window.location.href = 'mailto:partners@orivanta.ai';
  };

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/919473421755', '_blank');
  };

  const handleSkypeClick = () => {
    window.location.href = 'skype:kanhai.tech?chat';
  };

  const contactMethods = [
    {
      title: "Phone Support",
      description: "Call us directly for immediate assistance",
      icon: FaPhone,
      value: "+91 9473421755",
      action: "Call Now",
      color: "blue.500",
      onClick: handlePhoneClick
    },
    {
      title: "Email Support",
      description: "Send us an email for detailed inquiries",
      icon: FaEnvelope,
      value: "partners@orivanta.ai",
      action: "Send Email",
      color: "purple.500",
      onClick: handleEmailClick
    },
    {
      title: "WhatsApp",
      description: "Chat with us on WhatsApp",
      icon: FaWhatsapp,
      value: "+91 9473421755",
      action: "Chat Now",
      color: "teal.500",
      onClick: handleWhatsAppClick
    },
    {
      title: "Skype",
      description: "Connect with us on Skype",
      icon: FaSkype,
      value: "orivanta.ai",
      action: "Connect",
      color: "green.500",
      onClick: handleSkypeClick
    }
  ];

  const responseTimes = [
    {
      title: "Phone Support",
      time: "24/7",
      description: "Our phone lines are open round the clock for urgent matters",
      icon: FaPhone,
      color: "blue.500"
    },
    {
      title: "Email Response",
      time: "Within 24 hours",
      description: "We typically respond to emails within one business day",
      icon: FaEnvelope,
      color: "purple.500"
    },
    {
      title: "WhatsApp",
      time: "Within 2 hours",
      description: "Quick responses during business hours",
      icon: FaWhatsapp,
      color: "teal.500"
    },
    {
      title: "Live Chat",
      time: "Instant",
      description: "Connect with our support team instantly during business hours",
      icon: FaComments,
      color: "green.500"
    }
  ];

  const faqs = [
    {
      question: "What services do you offer?",
      answer: "We offer a comprehensive range of software development services including web development, mobile app development, custom software solutions, and IT consulting services."
    },
    {
      question: "How can I get started with your services?",
      answer: "Getting started is easy! Simply fill out our contact form or give us a call. We'll schedule a consultation to understand your needs and provide a tailored solution."
    },
    {
      question: "What is your typical project timeline?",
      answer: "Project timelines vary depending on the scope and complexity. We'll provide a detailed timeline during our initial consultation and keep you updated throughout the project."
    },
    {
      question: "Do you provide ongoing support?",
      answer: "Yes, we offer comprehensive support and maintenance services to ensure your software continues to run smoothly after deployment."
    }
  ];

  return (
    <Box pt={isEmbeddedAsForm ? "0" : "130px"} minH="calc(100vh - 130px - 80px)">
      {isEmbeddedAsForm ? (
        <Appointment />
      ) : (
        <>
          {/* Hero Section */}
          <Box
            backgroundImage="url(https://t3.ftcdn.net/jpg/05/30/96/04/360_F_530960431_c8fPd3HansYvrSJ4fJxZqp9OhjQmYoll.jpg)"
            backgroundSize="cover"
            backgroundPosition="center"
            backgroundAttachment="scroll"
            top="-14"
            height="60vh"
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="relative"
            overflow="hidden"
          >
            <Box
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              bg="rgba(0, 0, 0, 0.6)"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              color="white"
              zIndex="1"
            >
              <Heading
                as="h1"
                size="2xl"
                mb={4}
                textAlign="center"
                bgGradient="linear(to-r, blue.400, purple.500)"
                bgClip='text'
                fontWeight="bold"
                color='transparent'
              >
                Contact Us
              </Heading>
              <Text fontSize="xl" textAlign="center" maxW="600px" px={4}>
                Get in touch with us for any inquiries about our services or to schedule a consultation
              </Text>
            </Box>
          </Box>

          {/* Intro Section */}
          <Container maxW="container.md" py={10} textAlign="center">
            <Heading 
              size="lg" 
              mb={4}
              bgGradient="linear(to-r, blue.400, purple.500)"
              bgClip='text'
              fontWeight="bold"
            >
              We're At Your Service
            </Heading>
            <Divider orientation="horizontal" borderColor={primaryColor} borderWidth="2px" w="80px" mx="auto" mb={6} />
            <Text fontSize="lg" color="gray.600" mb={4}>
              If you are looking to contact us for further information on our services, any general questions, 
              or to discuss your needs and options, please contact us and we will be happy to provide you with 
              all the information you need.
            </Text>
            <Text fontSize="lg" fontWeight="bold" color={primaryColor}>Thank you for your interest in kanhai tech.</Text>
          </Container>

          {/* Quick Response Section */}
          <Box bg={bgColor} py={16}>
            <Container maxW="container.xl">
              <VStack spacing={12}>
                <VStack spacing={4}>
                  <Heading 
                    size="2xl" 
                    textAlign="center"
                    bgGradient="linear(to-r, blue.400, purple.500)"
                    bgClip='text'
                    fontWeight="bold"
                  >
                    Quick Response Times
                  </Heading>
                  <Divider orientation="horizontal" borderColor={primaryColor} borderWidth="2px" w="80px" mx="auto" />
                  <Text fontSize="lg" color="gray.600" textAlign="center" maxW="600px">
                    We value your time and ensure prompt responses through all our communication channels
                  </Text>
                </VStack>

                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8} w="100%">
                  {responseTimes.map((item, index) => (
                    <Box
                      key={index}
                      bg={cardBg}
                      p={6}
                      borderRadius="lg"
                      boxShadow="lg"
                      transition="all 0.3s ease"
                      _hover={{ transform: 'translateY(-5px)', boxShadow: 'xl' }}
                    >
                      <VStack spacing={4} align="center">
                        <Box
                          p={3}
                          borderRadius="full"
                          bg={`${item.color.split('.')[0]}.50`}
                          color={item.color}
                        >
                          <Icon as={item.icon} w={8} h={8} />
                        </Box>
                        <Heading size="md" textAlign="center">{item.title}</Heading>
                        <Text fontSize="xl" fontWeight="bold" color={item.color}>{item.time}</Text>
                        <Text textAlign="center" color="gray.600">{item.description}</Text>
                      </VStack>
                    </Box>
                  ))}
                </SimpleGrid>
              </VStack>
            </Container>
          </Box>

          {/* Get in Touch Section */}
          <Box py={16}>
            <Container maxW="container.xl">
              <VStack spacing={12}>
                <VStack spacing={4}>
                  <Heading 
                    size="2xl" 
                    textAlign="center"
                    bgGradient="linear(to-r, blue.400, purple.500)"
                    bgClip='text'
                    fontWeight="bold"
                  >
                    Get in Touch
                  </Heading>
                  <Divider orientation="horizontal" borderColor="blue.500" borderWidth="2px" w="80px" mx="auto" />
                  <Text fontSize="lg" color="gray.600" textAlign="center" maxW="600px">
                    Choose your preferred method of communication
                  </Text>
                </VStack>

                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8} w="100%">
                  {contactMethods.map((method, index) => (
                    <Box
                      key={index}
                      bg={cardBg}
                      p={6}
                      borderRadius="lg"
                      boxShadow="lg"
                      transition="all 0.3s ease"
                      _hover={{ transform: 'translateY(-5px)', boxShadow: 'xl' }}
                    >
                      <VStack spacing={4} align="center">
                        <Box
                          p={3}
                          borderRadius="full"
                          bg={`${method.color.split('.')[0]}.50`}
                          color={method.color}
                        >
                          <Icon as={method.icon} w={8} h={8} />
                        </Box>
                        <Heading size="md" textAlign="center">{method.title}</Heading>
                        <Text textAlign="center" color="gray.600">{method.description}</Text>
                        <Text fontSize="lg" fontWeight="bold" color={method.color}>{method.value}</Text>
                        <Button
                          colorScheme={method.color.split('.')[0]}
                          variant="outline"
                          size="sm"
                          leftIcon={<Icon as={method.icon} />}
                          onClick={method.onClick}
                          cursor="pointer"
                          _hover={{
                            bg: `${method.color.split('.')[0]}.50`,
                            transform: 'scale(1.05)'
                          }}
                        >
                          {method.action}
                        </Button>
                      </VStack>
                    </Box>
                  ))}
                </SimpleGrid>
              </VStack>
            </Container>
          </Box>

          {/* Contact Info and Form Section */}
          <Container maxW="container.xl" py={10}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
              {/* Contact Information Side */}
              <Box bg={cardBg} p={8} borderRadius="lg" boxShadow="lg">
                <VStack spacing={8} align={{ base: 'center', md: 'flex-start' }}>
                  {/* Why Choose Us */}
                  <Box w="100%">
                    <Heading 
                      size="md" 
                      mb={4}
                      bgGradient="linear(to-r, blue.400, purple.500)"
                      bgClip='text'
                      fontWeight="bold"
                    >
                      Why Choose Us
                    </Heading>
                    <VStack align="start" spacing={4}>
                      <HStack spacing={3}>
                        <Icon as={FaCheck} color="green.500" />
                        <Text>Expert Team of Developers</Text>
                      </HStack>
                      <HStack spacing={3}>
                        <Icon as={FaCheck} color="green.500" />
                        <Text>Quality Assurance Guaranteed</Text>
                      </HStack>
                      <HStack spacing={3}>
                        <Icon as={FaCheck} color="green.500" />
                        <Text>Timely Project Delivery</Text>
                      </HStack>
                      <HStack spacing={3}>
                        <Icon as={FaCheck} color="green.500" />
                        <Text>24/7 Technical Support</Text>
                      </HStack>
                      <HStack spacing={3}>
                        <Icon as={FaCheck} color="green.500" />
                        <Text>Competitive Pricing</Text>
                      </HStack>
                    </VStack>
                  </Box>

                  <Divider />

                  {/* Business Hours */}
                  <Box w="100%">
                    <Heading 
                      size="md" 
                      mb={4}
                      bgGradient="linear(to-r, blue.400, purple.500)"
                      bgClip='text'
                      fontWeight="bold"
                    >
                      Business Hours
                    </Heading>
                    <VStack align="start" spacing={2}>
                      <HStack>
                        <Icon as={FaClock} color="green.500" />
                        <Text>Monday - Friday: 9:00 AM - 6:00 PM</Text>
                      </HStack>
                      <HStack>
                        <Icon as={FaClock} color="green.500" />
                        <Text>Saturday: 10:00 AM - 4:00 PM</Text>
                      </HStack>
                      <HStack>
                        <Icon as={FaClock} color="green.500" />
                        <Text>Sunday: Closed</Text>
                      </HStack>
                    </VStack>
                  </Box>

                  <Divider />

                  {/* Social Media Links */}
                  <Box w="100%">
                    <Heading 
                      size="md" 
                      mb={4}
                      bgGradient="linear(to-r, blue.400, purple.500)"
                      bgClip='text'
                      fontWeight="bold"
                    >
                      Connect With Us
                    </Heading>
                    <HStack spacing={4}>
                      <IconButton
                        aria-label="LinkedIn"
                        icon={<FaLinkedin />}
                        colorScheme="blue"
                        variant="ghost"
                        size="lg"
                        _hover={{ bg: "blue.50", transform: "scale(1.1)" }}
                      />
                      <IconButton
                        aria-label="Twitter"
                        icon={<FaTwitter />}
                        colorScheme="purple"
                        variant="ghost"
                        size="lg"
                        _hover={{ bg: "purple.50", transform: "scale(1.1)" }}
                      />
                      <IconButton
                        aria-label="Facebook"
                        icon={<FaFacebook />}
                        colorScheme="teal"
                        variant="ghost"
                        size="lg"
                        _hover={{ bg: "teal.50", transform: "scale(1.1)" }}
                      />
                      <IconButton
                        aria-label="Instagram"
                        icon={<FaInstagram />}
                        colorScheme="pink"
                        variant="ghost"
                        size="lg"
                        _hover={{ bg: "pink.50", transform: "scale(1.1)" }}
                      />
                    </HStack>
                  </Box>

                  {/* Quick Contact */}
                  <Box w="100%">
                    <Heading 
                      size="md" 
                      mb={4}
                      bgGradient="linear(to-r, blue.400, purple.500)"
                      bgClip='text'
                      fontWeight="bold"
                    >
                      Quick Contact
                    </Heading>
                    <VStack align="start" spacing={3}>
                      <HStack spacing={3}>
                        <Icon as={FaPhone} color="blue.500" />
                        <Text>+91 9473421755</Text>
                      </HStack>
                      <HStack spacing={3}>
                        <Icon as={FaEnvelope} color="purple.500" />
                        <Text>partners@orivanta.ai</Text>
                      </HStack>
                    </VStack>
                  </Box>
                </VStack>
              </Box>

              {/* Appointment Form Side */}
              <Box bg={cardBg} p={8} borderRadius="lg" boxShadow="lg">
                <Appointment />
              </Box>
            </SimpleGrid>
          </Container>

          {/* FAQ Section */}
          <Box bg={bgColor} py={16}>
            <Container maxW="container.xl">
              <VStack spacing={8}>
                <Heading 
                  size="xl" 
                  textAlign="center"
                  bgGradient="linear(to-r, blue.400, purple.500)"
                  bgClip='text'
                  fontWeight="bold"
                >
                  Frequently Asked Questions
                </Heading>
                <Divider orientation="horizontal" borderColor="purple.500" borderWidth="2px" w="80px" mx="auto" />
                <Accordion allowToggle w="100%" maxW="800px">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={index} border="none" mb={4}>
                      <Box
                        bg={cardBg}
                        borderRadius="lg"
                        boxShadow="md"
                        _hover={{ boxShadow: "lg" }}
                        transition="all 0.3s ease"
                      >
                        <AccordionButton
                          py={4}
                          _hover={{ bg: 'gray.50' }}
                          borderRadius="lg"
                        >
                          <Box flex="1" textAlign="left" fontWeight="bold" color="blue.500">
                            {faq.question}
                          </Box>
                          <AccordionIcon color="purple.500" />
                        </AccordionButton>
                        <AccordionPanel pb={4} px={6}>
                          <Text color="gray.600">{faq.answer}</Text>
                        </AccordionPanel>
                      </Box>
                    </AccordionItem>
                  ))}
                </Accordion>
              </VStack>
            </Container>
          </Box>

          {/* Map Section */}
          <Box bg={bgColor} py={10}>
            <Container maxW="container.xl">
              <Heading 
                size="lg" 
                mb={6} 
                textAlign="center"
                bgGradient="linear(to-r, blue.400, purple.500)"
                bgClip='text'
                fontWeight="bold"
              >
                Find Us
              </Heading>
              <Box
                height="400px"
                borderRadius="lg"
                overflow="hidden"
                boxShadow="lg"
                _hover={{ boxShadow: "xl" }}
                transition="all 0.3s ease"
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.598130985754!2d77.5945627!3d12.9715987!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae1670c9b44e6d%3A0xf8dfc3e8517e4fe0!2sBengaluru%2C%20Karnataka!5e0!3m2!1sen!2sin!4v1647881234567!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </Box>
            </Container>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Contact;
